import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded font-medium whitespace-nowrap border",
  {
    variants: {
      tone: {
        neutral: "bg-gray-3 text-gray-11 border-transparent",
        accent: "bg-accent-3 text-accent-11 border-transparent",
        success: "bg-success-2 text-success-11 border-success-4",
        warning: "bg-warning-2 text-warning-11 border-warning-4",
        danger: "bg-danger-2 text-danger-11 border-danger-4",
        purple: "bg-purple-2 text-purple-11 border-purple-4",
        outline: "bg-transparent text-text-muted border-border",
      },
      size: {
        sm: "h-4 px-1.5 text-2xs",
        md: "h-5 px-2 text-xs",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  },
);

export type Tone = NonNullable<VariantProps<typeof badgeVariants>["tone"]>;

export function Badge({
  className, tone, size, ...props
}: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ tone, size }), className)} {...props} />;
}

/** A tone's solid colour: the dot beside a label, the stripe down a block's edge. */
const TONE_SOLID: Record<Tone, string> = {
  neutral: "bg-gray-9",
  accent: "bg-accent-9",
  success: "bg-success-9",
  warning: "bg-warning-9",
  danger: "bg-danger-9",
  purple: "bg-purple-9",
  outline: "bg-gray-7",
};

/** A dot in a tone, beside the words it colours; never in place of them. */
export function ToneDot({ tone, className }: { tone: Tone; className?: string }) {
  return <span aria-hidden className={cn("inline-block size-2 shrink-0 rounded-full", TONE_SOLID[tone], className)} />;
}

/**
 * A block in a tone (the calendar's appointments, ACL-525): the badge's fill, edge and ink, and a
 * stripe in the solid colour down its left edge, so a chip on the calendar and the badge in its
 * details read as the same colour.
 */
const TONE_BLOCK: Record<Tone, string> = {
  neutral: "bg-gray-2 border-gray-4 border-l-gray-9 text-gray-11",
  accent: "bg-accent-2 border-accent-4 border-l-accent-9 text-accent-11",
  success: "bg-success-2 border-success-4 border-l-success-9 text-success-11",
  warning: "bg-warning-2 border-warning-4 border-l-warning-9 text-warning-11",
  danger: "bg-danger-2 border-danger-4 border-l-danger-9 text-danger-11",
  purple: "bg-purple-2 border-purple-4 border-l-purple-9 text-purple-11",
  outline: "bg-surface border-dashed border-gray-6 border-l-gray-8 text-text-muted",
};

export function toneBlock(tone: Tone): string {
  return cn("border border-l-[3px]", TONE_BLOCK[tone]);
}

/**
 * A registration/health dot with three states, not two.
 *
 * `null` is "we could not reach the live-state store", which must not look the same as
 * "offline" — one means check the infrastructure, the other means call the rep.
 */
export function StatusDot({
  state, className, label,
}: {
  state: boolean | null;
  className?: string;
  label?: string;
}) {
  const text = label ?? (state === null ? "Unknown" : state ? "Registered" : "Offline");
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)} title={text}>
      <span
        className={cn(
          "relative inline-block size-1.5 rounded-full",
          state === null && "bg-gray-7",
          state === true && "bg-success-9",
          state === false && "bg-gray-7 ring-1 ring-inset ring-gray-8",
        )}
        aria-hidden
      />
      <span className="sr-only">{text}</span>
    </span>
  );
}
